import 'phoenix_html'

// Establish Phoenix Socket and LiveView configuration.
import { Socket, LongPoll } from 'phoenix'
import { LiveSocket } from 'phoenix_live_view'
import topbar from 'topbar'
import { initFacebookPixel, initGoogleTag, initSegment } from './analytics'
import { OSANO_CATEGORY, onConsentChange, registerConsentCookieCleanup } from './consent'
import { syncHubSpotTracking } from './hubspot.js'
import { Hooks } from './hooks'

const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute('content')

const urlParams = new URLSearchParams(document.location.search)
const liveSocketTransport = urlParams.get('transport') === 'longpoll' ? LongPoll : WebSocket

// Create the LiveSocket
const liveSocket = new LiveSocket('/live', Socket, {
  params: { _csrf_token: csrfToken },
  hooks: Hooks,
  longpollerTimeout: 45000, // milliseconds
  transport: liveSocketTransport,
})

// Setup for long-polling fallback
const socket = liveSocket.socket
const originalOnConnError = socket.onConnError
let fallbackToLongPoll = true

socket.onOpen(() => {
  fallbackToLongPoll = false
})

socket.onConnError = (...args) => {
  if (fallbackToLongPoll) {
    // No longer fallback to long polling
    fallbackToLongPoll = false
    // Close the socket with an error code
    socket.disconnect(null, 3000)
    // Set transport to `LongPoll`
    socket.transport = LongPoll
    // Attempt to reconnect using `LongPoll`
    socket.connect()
  } else {
    originalOnConnError.apply(socket, args)
  }
}

// Show progress bar on live navigation and form submits
topbar.config({ barColors: { 0: '#29d' }, shadowColor: 'rgba(0, 0, 0, .3)' })
window.addEventListener('phx:page-loading-start', info => topbar.show())
window.addEventListener('phx:page-loading-stop', info => {
  topbar.hide()

  if (info.detail.kind === 'element' && info.detail.target.dataset.papaScrollTop) {
    window.scrollTo(0, 0)
  }
})

liveSocket.connect()

window.liveSocket = liveSocket

// Runs `callback` exactly once, as soon as the DOM is ready. Only registers the
// DOMContentLoaded listener while the document is still loading; otherwise it
// invokes immediately. Registering the listener AND calling immediately would
// double-fire in the `interactive` window (before DOMContentLoaded), which would
// register duplicate Osano listeners via onConsentChange.
const onReady = callback => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true })
  } else {
    callback()
  }
}

onReady(() => {
  // When Osano is disabled (e.g. lower environments) there's no consent manager
  // to gate on, so initialize tracking directly — analytics stays testable.
  if (window.OSANO_DISABLED) {
    initSegment()
    initFacebookPixel()
    initGoogleTag({ analytics: true, marketing: true })
    syncHubSpotTracking({ analytics: true, marketing: true, personalization: true })
    return
  }

  // Cookie cleanup + reload-on-withdrawal. Runs its own baseline sweep when
  // Osano is (or becomes) initialized.
  registerConsentCookieCleanup()

  // Gate tracker init on Osano consent instead of firing unconditionally.
  // Fires immediately if Osano is already initialized, and on every consent
  // save. init fns are idempotent, so re-calling on a later grant is safe.
  //   analytics -> Segment ;  marketing -> direct Meta Pixel + HubSpot analytics.
  onConsentChange(consent => {
    if (consent[OSANO_CATEGORY.ANALYTICS]) initSegment()
    if (consent[OSANO_CATEGORY.MARKETING]) initFacebookPixel()
    syncHubSpotTracking({
      analytics: consent[OSANO_CATEGORY.ANALYTICS],
      marketing: consent[OSANO_CATEGORY.MARKETING],
      personalization: consent[OSANO_CATEGORY.PERSONALIZATION],
    })
    // GA4 (analytics) + Google Ads (marketing) share one gtag.js. initGoogleTag
    // does its own per-category gating and idempotency, so call it every time.
    initGoogleTag({
      analytics: consent[OSANO_CATEGORY.ANALYTICS],
      marketing: consent[OSANO_CATEGORY.MARKETING],
    })
  })
})
